import type { ImportRow, ParseError, ParseResult, OfxMetadata } from '../types';

// ------------------------------------------------------------
// Parser OFX manual — sem deps externas.
//
// OFX SGML (versões 1.x) e XML (2.x) compartilham o mesmo modelo de tags.
// Estratégia:
//  - Strip o cabeçalho `OFXHEADER:.../<OFX>...</OFX>` (ou só o conteúdo se SGML
//    sem fechamento explícito).
//  - Para extrair valores, usar regex pra <TAG>valor (SGML) ou <TAG>valor</TAG>.
//  - Trabalhar transação a transação dentro de <STMTTRN>...</STMTTRN>.
// ------------------------------------------------------------

/** Extrai conteúdo de uma tag (primeira ocorrência). Aceita SGML e XML. */
function tagValue(text: string, tag: string): string | undefined {
  // SGML: <TAG>valor (até próxima tag ou fim de linha)
  // XML:  <TAG>valor</TAG>
  const re = new RegExp(`<${tag}>([^<\\r\\n]*)`, 'i');
  const m = text.match(re);
  if (!m) return undefined;
  return m[1]?.trim();
}

/** Extrai todas as ocorrências do bloco entre <BLOCK> e </BLOCK>. */
function extractBlocks(text: string, tag: string): string[] {
  const re = new RegExp(`<${tag}>([\\s\\S]*?)</${tag}>`, 'gi');
  const out: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m[1] !== undefined) out.push(m[1]);
  }
  return out;
}

/** Encontra o primeiro bloco entre <BLOCK> e </BLOCK>, ou undefined. */
function firstBlock(text: string, tag: string): string | undefined {
  const blocks = extractBlocks(text, tag);
  return blocks[0];
}

/** Parse de DTPOSTED OFX (YYYYMMDD ou YYYYMMDDHHmmss[+/-TZ]). */
function parseOfxDate(raw: string | undefined): Date | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (trimmed.length < 8) return null;
  const y = parseInt(trimmed.slice(0, 4), 10);
  const m = parseInt(trimmed.slice(4, 6), 10);
  const d = parseInt(trimmed.slice(6, 8), 10);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
}

function parseOfxAmount(raw: string | undefined): number | null {
  if (!raw) return null;
  const cleaned = raw.trim().replace(',', '.');
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : null;
}

/**
 * Parseia conteúdo OFX em uma estrutura `ParseResult` compatível com o ImportWizard.
 * Mapeia STMTTRN → ImportRow:
 *  - DTPOSTED → dueDate e paymentDate (extrato = já aconteceu)
 *  - TRNAMT  → type (sinal: +income / −expense) e amount (abs)
 *  - NAME + MEMO → description (concat)
 *  - FITID   → notes (id único do banco; útil para detecção de duplicatas futura)
 *  - status  → sempre 'completed'
 */
export function parseOFXContent(text: string): ParseResult {
  const errors: ParseError[] = [];
  const rows: ImportRow[] = [];
  const metadata: OfxMetadata = {};

  // Pega só do <OFX> em diante (descarta cabeçalho key:value)
  const ofxStart = text.search(/<OFX>/i);
  const body = ofxStart >= 0 ? text.slice(ofxStart) : text;

  // ---- Metadata ----
  const stmtRsBlock = firstBlock(body, 'STMTRS') ?? firstBlock(body, 'CCSTMTRS');
  if (!stmtRsBlock) {
    errors.push({
      line: 0,
      reason: 'Arquivo OFX não contém transações bancárias reconhecidas (STMTRS/CCSTMTRS).',
    });
    return { rows, errors, uniqueCategories: [], uniqueCostCenters: [] };
  }

  const curdef = tagValue(stmtRsBlock, 'CURDEF');
  if (curdef) metadata.currency = curdef;

  const acctBlock =
    firstBlock(stmtRsBlock, 'BANKACCTFROM') ?? firstBlock(stmtRsBlock, 'CCACCTFROM');
  if (acctBlock) {
    const bankId = tagValue(acctBlock, 'BANKID');
    const acctId = tagValue(acctBlock, 'ACCTID');
    const acctType = tagValue(acctBlock, 'ACCTTYPE');
    if (bankId) metadata.bankId = bankId;
    if (acctId) metadata.accountId = acctId;
    if (acctType) metadata.accountType = acctType;
  }

  const tranList = firstBlock(stmtRsBlock, 'BANKTRANLIST') ?? '';
  if (tranList) {
    const dtStart = tagValue(tranList, 'DTSTART');
    const dtEnd = tagValue(tranList, 'DTEND');
    const start = parseOfxDate(dtStart);
    const end = parseOfxDate(dtEnd);
    if (start) metadata.startDate = start;
    if (end) metadata.endDate = end;
  }

  // ---- Transações ----
  const sourceForTrns = tranList || stmtRsBlock;
  const trnBlocks = extractBlocks(sourceForTrns, 'STMTTRN');

  if (trnBlocks.length === 0) {
    errors.push({ line: 0, reason: 'Extrato OFX não contém transações (STMTTRN).' });
    return { rows, errors, uniqueCategories: [], uniqueCostCenters: [], ofxMetadata: metadata };
  }

  trnBlocks.forEach((block, idx) => {
    const lineNumber = idx + 1;

    const date = parseOfxDate(tagValue(block, 'DTPOSTED'));
    if (!date) {
      errors.push({
        line: lineNumber,
        reason: `Data inválida (DTPOSTED="${tagValue(block, 'DTPOSTED') ?? ''}")`,
      });
      return;
    }

    const rawAmount = parseOfxAmount(tagValue(block, 'TRNAMT'));
    if (rawAmount === null || rawAmount === 0) {
      errors.push({
        line: lineNumber,
        reason: `Valor inválido (TRNAMT="${tagValue(block, 'TRNAMT') ?? ''}")`,
      });
      return;
    }

    const type: ImportRow['type'] = rawAmount >= 0 ? 'income' : 'expense';
    const amount = Math.abs(rawAmount);

    const name = (tagValue(block, 'NAME') ?? '').trim();
    const memo = (tagValue(block, 'MEMO') ?? '').trim();
    let description: string;
    if (name && memo && name.toLowerCase() !== memo.toLowerCase()) {
      description = `${name} — ${memo}`;
    } else if (name) {
      description = name;
    } else if (memo) {
      description = memo;
    } else {
      description = 'Movimentação bancária';
    }

    const fitid = (tagValue(block, 'FITID') ?? '').trim();

    rows.push({
      lineNumber,
      dueDate: date,
      paymentDate: date,
      description,
      amount,
      status: 'completed',
      type,
      // FITID vira externalId pra detecção de duplicata em re-import.
      // Mantemos também em notes pra rastreabilidade visual.
      ...(fitid ? { externalId: fitid, notes: `FITID: ${fitid}` } : {}),
    });
  });

  return {
    rows,
    errors,
    uniqueCategories: [],
    uniqueCostCenters: [],
    ofxMetadata: metadata,
  };
}

export async function parseOFXFile(file: File): Promise<ParseResult> {
  const text = await file.text();
  return parseOFXContent(text);
}
